import {
  AttachMoneyOutlined,
  ContentCopy,
  HowToReg,
  LockOutlined,
  PersonSearch,
  ProductionQuantityLimits,
  RecordVoiceOver,
  ReportProblem,
  RunningWithErrors,
  SecurityOutlined as SecurityIcon,
  VisibilityOff,
} from "@mui/icons-material";
import { Box, GridLegacy as Grid } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import AnalyticsCard from "@/components/common/AnalyticsCard";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import { useSuspiciousActivityAlerts } from "@/hooks/useSuspiciousActivityAlerts";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import SuspiciousActivityAlertsTable from "@/pages/reports/suspicious-activity/SuspiciousActivityAlertsTable";
import type { AlertStatus } from "@/services/features/AnalyticsServiceV2";
import { formatInteger, formatPercentFromWhole } from "@/utils/number";

const SuspiciousActivityReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    selectionLoading,
    period,
    customRange,
    currentDateRange,
    deviceType,
    deviceTypeFilterDisabled,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
    onDeviceTypeChange,
  } = useStoreInsightsReportFilters({
    enableDeviceFilter: true,
    defaultDeviceType: "all",
  });
  const alertTypeConfig = useMemo(
    () =>
      [
        {
          type: "DUPLICATE_ITEM_FAMILY",
          title: t("storeInsights.suspiciousActivityReport.alertTypes.sameItem"),
          icon: <ContentCopy />,
        },
        {
          type: "RESTRICTED_CATEGORY",
          title: t("storeInsights.suspiciousActivityReport.alertTypes.restrictedItems"),
          icon: <LockOutlined />,
        },
        {
          type: "TOO_MANY_SKUS",
          title: t("storeInsights.suspiciousActivityReport.alertTypes.unusuallyHighItemCount"),
          icon: <ProductionQuantityLimits />,
        },
        {
          type: "VALUE_TOO_HIGH",
          title: t("storeInsights.suspiciousActivityReport.alertTypes.valueTooHigh"),
          icon: <AttachMoneyOutlined />,
        },
      ] as const,
    [t],
  );
  const serviceStatusConfig = useMemo<
    Array<{ status: AlertStatus; label: string; icon: React.ReactNode }>
  >(
    () => [
      {
        status: "CONFIRMED",
        label: t("storeInsights.suspiciousActivityReport.serviceStatus.concernsObserved"),
        icon: <PersonSearch />,
      },
      {
        status: "ALL_CLEAR",
        label: t("storeInsights.suspiciousActivityReport.serviceStatus.noConcern"),
        icon: <HowToReg />,
      },
      {
        status: "UNABLE_TO_ASSESS",
        label: t("storeInsights.suspiciousActivityReport.serviceStatus.unableToAssess"),
        icon: <VisibilityOff />,
      },
      {
        status: "MISSED",
        label: t("storeInsights.suspiciousActivityReport.serviceStatus.missed"),
        icon: <RunningWithErrors />,
      },
    ],
    [t],
  );

  const alerts = useSuspiciousActivityAlerts(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const networkLoading = getReportNetworkLoading(loading, selectionLoading, alerts.loading);
  const contentLoading = networkLoading;
  const loadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
        deviceType,
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, deviceType, selectedStores],
  );
  const hasAlertDataError = Boolean(alerts.error);

  const totalAlerts = alerts.data.length;

  const alertTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    alerts.data.forEach((alert) => {
      const types = new Set(alert.suspiciousActivities.map((activity) => activity.type));
      types.forEach((type) => {
        counts[type] = (counts[type] ?? 0) + 1;
      });
    });

    return counts;
  }, [alerts.data]);

  const statusCounts = useMemo(() => {
    const counts: Record<AlertStatus, number> = {
      CREATED: 0,
      ACCEPTED: 0,
      ALL_CLEAR: 0,
      MISSED: 0,
      CONFIRMED: 0,
      UNABLE_TO_ASSESS: 0,
    };

    alerts.data.forEach((alert) => {
      counts[alert.status] = (counts[alert.status] ?? 0) + 1;
    });

    return counts;
  }, [alerts.data]);

  const missedCount = statusCounts.MISSED ?? 0;
  const respondedCount = Math.max(totalAlerts - missedCount, 0);
  const responseRate = totalAlerts > 0 ? Math.round((respondedCount / totalAlerts) * 100) : 0;

  const storeTimezones = useMemo(
    () => Object.fromEntries(selectedStores.map((store) => [store.id, store.timezone])),
    [selectedStores],
  );
  const currentLocale = useMemo(
    () => new Intl.Locale(i18n.resolvedLanguage || i18n.language || "en-US"),
    [i18n.resolvedLanguage, i18n.language],
  );

  return (
    <Box sx={{ pb: 4 }}>
      <StoreInsightsReportHeader
        title={t("storeInsights.suspiciousActivityReport.title")}
        icon={<SecurityIcon />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled={deviceTypeFilterDisabled}
        selectedDeviceType={deviceType}
        onDeviceTypeChange={onDeviceTypeChange}
      />

      <Box sx={{ p: { xs: 2, md: 2.5 }, pt: 0, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2 className="text-[20px] font-semibold mb-4">
              {t("storeInsights.suspiciousActivityReport.sections.totalAlerts")}
            </h2>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[300px]">
                <AnalyticsCard
                  value={
                    contentLoading || hasAlertDataError
                      ? "--"
                      : formatInteger(totalAlerts, currentLocale)
                  }
                  previousText={null}
                  title={t("storeInsights.suspiciousActivityReport.cards.alertsTriggered")}
                  isPositive={false}
                  subtitle=""
                  icon={<ReportProblem />}
                  pillText={null}
                  showGradient={true}
                />
              </div>

              {alertTypeConfig.map((card) => {
                const count = alertTypeCounts[card.type] ?? 0;

                return (
                  <div key={card.type} className="flex-1 min-w-[300px]">
                    <AnalyticsCard
                      value={
                        contentLoading || hasAlertDataError
                          ? "--"
                          : formatInteger(count, currentLocale)
                      }
                      previousText={null}
                      title={card.title}
                      isPositive={false}
                      subtitle=""
                      icon={card.icon}
                      pillText={null}
                      showGradient={true}
                    />
                  </div>
                );
              })}
            </div>
          </Grid>

          <Grid item xs={12}>
            <h2 className="text-[20px] font-semibold mb-4">
              {t("storeInsights.suspiciousActivityReport.sections.service")}
            </h2>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[300px]">
                <AnalyticsCard
                  value={
                    contentLoading || hasAlertDataError
                      ? "--"
                      : formatInteger(respondedCount, currentLocale)
                  }
                  previousText={null}
                  title={t("storeInsights.suspiciousActivityReport.cards.alertsResponded")}
                  isPositive={responseRate > 0}
                  subtitle={
                    hasAlertDataError
                      ? ""
                      : t("storeInsights.suspiciousActivityReport.text.percentageOfTotalAlerts", {
                          percentage: formatPercentFromWhole(responseRate, currentLocale),
                        })
                  }
                  icon={<RecordVoiceOver />}
                  pillText={null}
                  showGradient={true}
                  subtitleLoading={contentLoading}
                />
              </div>

              {serviceStatusConfig.map((row) => {
                const count = statusCounts[row.status] ?? 0;
                const percentage = totalAlerts > 0 ? Math.round((count / totalAlerts) * 100) : 0;

                return (
                  <div key={row.status} className="flex-1 min-w-[300px]">
                    <AnalyticsCard
                      value={
                        contentLoading || hasAlertDataError
                          ? "--"
                          : formatInteger(count, currentLocale)
                      }
                      previousText={null}
                      title={row.label}
                      isPositive={row.status !== "MISSED"}
                      subtitle={
                        hasAlertDataError
                          ? ""
                          : t(
                              "storeInsights.suspiciousActivityReport.text.percentageOfTotalAlerts",
                              {
                                percentage: formatPercentFromWhole(percentage, currentLocale),
                              },
                            )
                      }
                      icon={row.icon}
                      pillText={null}
                      showGradient={true}
                      subtitleLoading={contentLoading}
                    />
                  </div>
                );
              })}
            </div>
          </Grid>

          <Grid item xs={12}>
            <SuspiciousActivityAlertsTable
              data={alerts.data}
              loading={contentLoading}
              error={alerts.error}
              storeTimezones={storeTimezones}
              loadingKey={loadingKey}
              storeCount={selectedStores.length}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SuspiciousActivityReport;
